import { sortBy } from "lodash";
import { SegmentsUtils } from "app/services/segments/SegmentsUtils";
import Sentry from "@similarweb/sentry-logger";
import DurationService from "app/services/DurationService";
import { swSettings } from "app/common/services/swSettings";
import type SegmentsApiService from "app/services/segments/segmentsWizardService";
import { DEFAULT_WORDS_FILTER_CONFIGS } from "app/services/segments/segmentsWizardService";
import type {
    IPopularSegments,
    IPureRule,
    IRule,
    ISegmentRuleItem,
    ISegmentsUrlParams,
    ISegmentUrl,
    ISite,
    IWordPredictions,
    IWordsFilterConfig,
} from "app/pages/segments/redesigned-wizard/segment-wizard/types";
import { ENewRuleType, RuleTypes } from "app/pages/segments/redesigned-wizard/segment-wizard/types";
import rulesQueryHandler from "pro-features/components/RulesQueryBuilder/src/handlers/rulesQueryHandler";
import { allTrackers } from "app/services/track/track";

const DEFAULT_WORD_PREDICTIONS_SORT_EXP = "Share+desc";
const DEFAULT_WORD_PREDICTIONS_TIME_GRANULARITY = "Monthly";

const prepareApiParams = (selectedSiteName: string): ISegmentsUrlParams => {
    const durationData = DurationService.getDurationData("18m", undefined, swSettings.current.componentId);
    return {
        from: durationData.forAPI.from,
        to: durationData.forAPI.to,
        includeSubDomains: true,
        isWindow: false,
        orderBy: DEFAULT_WORD_PREDICTIONS_SORT_EXP,
        timeGranularity: DEFAULT_WORD_PREDICTIONS_TIME_GRANULARITY,
        keys: selectedSiteName,
    };
};

const filterEmptyRules = (rules: IRule[]) => rules?.filter((rule) => doesAtLeastOneRuleExist(rule)) ?? [];

const filterEmptyRulesOnSave = (
    rulesToUpdate: IRule[],
    segmentIncludeRulesLength: number,
    shouldFilterEmptyValues?: boolean,
    newRuleType?: ENewRuleType,
) => {
    if (shouldFilterEmptyValues) {
        // Clear any empty rules if shouldFilterEmptyValues is true
        rulesToUpdate = filterEmptyRules(rulesToUpdate);
    } else if (
        segmentIncludeRulesLength > 1 &&
        (newRuleType === ENewRuleType.INCLUDE || newRuleType === ENewRuleType.EXCLUDE)
    ) {
        // Clear any *extra* empty rules (there should be tops one extra empty rule of each type)

        // Extract segmentIncludeRules except for its last cell
        let segmentIncludeRulesToUpdate = rulesToUpdate.slice(0, segmentIncludeRulesLength - 1);
        segmentIncludeRulesToUpdate = filterEmptyRules(segmentIncludeRulesToUpdate);
        let segmentExcludeRulesToUpdate = [];
        const segmentExcludeRulesLength = rulesToUpdate.length - segmentIncludeRulesLength;
        if (segmentExcludeRulesLength > 1) {
            segmentExcludeRulesToUpdate = rulesToUpdate.slice(segmentIncludeRulesLength, rulesToUpdate.length - 1);
            segmentExcludeRulesToUpdate = filterEmptyRules(segmentExcludeRulesToUpdate);
        }
        rulesToUpdate = segmentIncludeRulesToUpdate.concat(
            rulesToUpdate[segmentIncludeRulesLength - 1],
            segmentExcludeRulesToUpdate,
            rulesToUpdate[rulesToUpdate.length - 1],
        );
    }
    return rulesToUpdate;
};

// Add a new empty rule at the end of the rules list
// so that the user will always have a new empty rule at the bottom of the rules list
const addNewRuleIfNeeded = (
    segmentRules: IRule[],
    rulesToUpdate: IRule[],
    segmentIncludeRulesLength: number,
    isFirstSegmentEdit: boolean,
    newRuleType?: ENewRuleType,
) => {
    if (!isNaN(newRuleType)) {
        if (newRuleType === ENewRuleType.INCLUDE || newRuleType === ENewRuleType.EXCLUDE) {
            const convertedNewRuleType = newRuleType as unknown as RuleTypes;
            // Verify there isn't already an empty rule of the same type at the end of segmentsRules
            if (segmentRules.length >= 2 || isFirstSegmentEdit) {
                const emptyRuleOfSameType =
                    newRuleType === ENewRuleType.INCLUDE
                        ? segmentRules[segmentIncludeRulesLength - 1]
                        : segmentRules[segmentRules.length - 1];
                if (doesAtLeastOneRuleExist(emptyRuleOfSameType) || isFirstSegmentEdit) {
                    const newRule = createNewRule(convertedNewRuleType);
                    rulesToUpdate.push(newRule);
                }
            }
        } else if (newRuleType === ENewRuleType.BOTH) {
            const includeRule = createNewRule(ENewRuleType.INCLUDE as unknown as RuleTypes);
            const excludeRule = createNewRule(ENewRuleType.EXCLUDE as unknown as RuleTypes);
            rulesToUpdate.push(includeRule, excludeRule);
        }
    }
};

const prepareApiRules = (rules: IRule[], ruleIndex?: number) => {
    const rulesForApi = rules?.slice(0, ruleIndex ?? rules.length)?.filter((x) => doesAtLeastOneRuleExist(x)) ?? [];

    return (
        rulesForApi?.map((rule, idx) => {
            let ruleType: number;
            if (rule.type === RuleTypes.include) {
                ruleType = 0;
            } else if (rule.type === RuleTypes.exclude) {
                ruleType = 1;
            }
            const words = ruleIndex !== -1 && idx === ruleIndex ? [] : rule.words;
            const exact = ruleIndex !== -1 && idx === ruleIndex ? [] : rule.exact;
            const folders = ruleIndex !== -1 && idx === ruleIndex ? [] : rule.folders;
            const exactURLS = ruleIndex !== -1 && idx === ruleIndex ? [] : rule.exactURLS;
            return {
                words,
                exact,
                folders,
                exactURLS,
                type: ruleType,
            };
        }) ?? []
    );
};

const createNewRule = (
    ruleType = RuleTypes.include,
    ruleWords = [],
    ruleExact = [],
    ruleFolders = [],
    ruleExactURLS = [],
): IRule => {
    return {
        type: ruleType,
        words: ruleWords,
        exact: ruleExact,
        folders: ruleFolders,
        exactURLS: ruleExactURLS,
    };
};

const updateRule = (rules: IRule[], ruleIndex: number, rule: IRule): IRule[] => {
    return rules.map((curRule, idx) =>
        idx === ruleIndex
            ? {
                  ...curRule,
                  words: [...rule.words],
                  exact: [...rule.exact],
                  folders: [...rule.folders],
              }
            : curRule,
    );
};

const transformSegmentPreview = (data): ISegmentUrl[] => {
    return data.map((item) => {
        return {
            url: `https://${item.Page.URL}`,
            text: item.Page.URL,
            target: "_blank",
        };
    });
};

const getSegmentsPreview = async (
    rules: IRule[],
    segmentsApiService: SegmentsApiService,
    requestSegmentsPreview: (segmentsPreviewLoading: boolean) => void,
    selectedSite?: ISite,
): Promise<IPopularSegments> => {
    // Signal that the segments preview is being loaded
    // (used by the URL List for adding a loader when segments preview is being fetched)
    requestSegmentsPreview(true);
    const popularPagesRules = prepareApiRules(rules ?? []);
    const popularPages = await segmentsApiService.getPopularPages(popularPagesRules, selectedSite);
    // Signal that we're done loading the segments preview
    requestSegmentsPreview(false);

    return popularPages;
};

export const doesAtLeastOneRuleExist = (rule: IRule) =>
    rule?.words.length > 0 || rule?.exact.length > 0 || rule?.folders.length > 0 || rule?.exactURLS.length > 0;

export const doAllRuleFiltersDefined = (rule: IRule, isMidTierUser: boolean) =>
    rule?.words.length > 0 &&
    (isMidTierUser || (rule?.exact.length > 0 && rule?.folders.length > 0 && rule?.exactURLS.length > 0));

export const onSegmentSave = (
    segmentRules: IRule[],
    setSegmentRules: (segmentRules: IRule[]) => void,
    segmentIncludeRulesLength: number,
    isFirstSegmentEdit: boolean,
    shouldFilterEmptyValues?: boolean,
    newRuleType?: ENewRuleType,
    ruleIndex?: number,
    activeWordPredictionIndex?: number,
    setActiveWordPredictionIndex?: (number) => void,
) => {
    let rulesToUpdate = segmentRules ? [...segmentRules] : [];
    rulesToUpdate = filterEmptyRulesOnSave(
        rulesToUpdate,
        segmentIncludeRulesLength,
        shouldFilterEmptyValues,
        newRuleType,
    );

    addNewRuleIfNeeded(segmentRules, rulesToUpdate, segmentIncludeRulesLength, isFirstSegmentEdit, newRuleType);

    if (!isNaN(ruleIndex) && !isNaN(activeWordPredictionIndex) && ruleIndex < activeWordPredictionIndex) {
        setActiveWordPredictionIndex(null);
    }
    // Update rules on the Context store
    const orderedRulesToUpdate = sortBy(rulesToUpdate, ["type"]);
    setSegmentRules(orderedRulesToUpdate);
};

export const isWebsiteAllowed = (selectedSite: ISite, site?: ISite) => {
    site = site ?? selectedSite;

    if (!site) {
        return false;
    }
    const { validation } = site;
    if (!validation || validation.isInvalid || validation.isBlacklisted) {
        return false;
    }

    const isMidTierUser = SegmentsUtils.isMidTierUser();
    if (isMidTierUser) {
        return validation.isRobotsTxt && !validation.isInvalid;
    }
    return true;
};

export const validateSelectedSite = async (
    selectedSite: ISite,
    segmentsApiService: SegmentsApiService,
): Promise<ISite> => {
    const validatedSelectedSite: ISite = { ...selectedSite };
    if (!validatedSelectedSite.validation) {
        let validatedDomain;
        try {
            validatedDomain = (
                await segmentsApiService.isDomainValidForSegments({
                    keys: [selectedSite.name],
                })
            )[0];
            const isBlacklisted = !!validatedDomain.isBlacklisted;
            const isRobotsTxt = !!validatedDomain.isRobotsTxt;
            const isTooSmall = validatedDomain.blockStatus === 1;
            validatedSelectedSite.validation = {
                isBlacklisted,
                isRobotsTxt,
                isTooSmall,
                isInvalid: isBlacklisted,
            };
        } catch (err) {
            //black listed sites such as facebook.com
            Sentry.error("Error validating domain", err);
            validatedSelectedSite.validation = {
                isInvalid: true,
            };
        }

        if (!isWebsiteAllowed(validatedSelectedSite)) {
            allTrackers.dispatchTrackEvent(
                "Invalid email address",
                "click",
                `segment wizard/new/validation failed/${validatedSelectedSite.name || ""}`,
            );
        }
    }
    return validatedSelectedSite;
};

export const prepareSegments = async (
    site: ISite,
    segmentsApiService: SegmentsApiService,
    setSegmentsReady: (segmentsReady: boolean) => void,
) => {
    setSegmentsReady(false);
    try {
        const params = prepareApiParams(site.name);
        await segmentsApiService.loadPrepareSegments(params, site.validation.isRobotsTxt, true);
    } catch (e) {
        Sentry.error("Error preparing segments popular pages", e);
    } finally {
        setSegmentsReady(true);
    }
};

export const hasExistingUrlsCompoundStringNoRobotsTxt = async (
    rules: IRule[],
    requestSegmentsPreview: (segmentsPreviewLoading: boolean) => void,
    selectedSite: ISite,
    segmentsApiService: SegmentsApiService,
): Promise<boolean> => {
    // Signal that the segments preview are being loaded
    // (used by the URL List for adding a loader when segments preview are being fetched)
    const params = prepareApiParams(selectedSite?.name);
    const previewRules = prepareApiRules(rules ?? []);
    return segmentsApiService.hasExistingUrlsCompundStringNoRobots(params, previewRules);
};

export const getWordPredictions = async (
    rules: IRule[],
    segmentsApiService: SegmentsApiService,
    overrideWordsFilterConfig?: IWordsFilterConfig,
    ruleIndex?: number,
    selectedSite?: ISite,
): Promise<IWordPredictions> => {
    const predictionRules = prepareApiRules(rules, ruleIndex);
    const useWordsFilterConfig = {
        ...(!selectedSite?.validation?.isRobotsTxt && DEFAULT_WORDS_FILTER_CONFIGS.noRobotsTxt),
        ...overrideWordsFilterConfig,
    };

    return await segmentsApiService.getWordPredictions(predictionRules, useWordsFilterConfig);
};

export const getFolderPredictions = async (
    rules: IRule[],
    segmentsApiService: SegmentsApiService,
    ruleIndex?: number,
): Promise<IWordPredictions> => {
    const predictionRules = prepareApiRules(rules, ruleIndex);
    return await segmentsApiService.getFolderPredictions(predictionRules);
};

export const rulesSerializer = (res: IRule[], rule: IRule) => {
    const hasContent = doesAtLeastOneRuleExist(rule);

    if (hasContent) {
        res.push({
            words: rule.words,
            exact: rule.exact,
            folders: rule.folders,
            exactURLS: rule.exactURLS,
            type: rule.type,
        });
    }
    return res;
};

export const getRulesHighlightWords = (
    segmentsApiService: SegmentsApiService,
    segmentRules: ISegmentRuleItem[] = [],
): string[] => segmentsApiService.getRulesHighlightWords(segmentRules);

export const getRulesExactMatchHighlights = (
    segmentsApiService: SegmentsApiService,
    segmentRules: ISegmentRuleItem[] = [],
): string[] => segmentsApiService.getRulesExactMatchPhrases(segmentRules);

export const getSegmentShare = (
    domain: string,
    selectedCountry: number,
    segmentRules: IRule[],
    segmentsApiService: SegmentsApiService,
) => segmentsApiService.calculateSegmentShare(domain, selectedCountry, filterEmptyRules(segmentRules));

// Adds a string to the currently edited rule.
export const addItems = ({ words, exact, folders, exactURLS }: IPureRule, currentRule: IRule) => {
    // We keep here array of rules and ruleIndex, simply in order not to change rulesQueryHandler methods
    let updatedRules = [...[currentRule]];

    if (words) {
        updatedRules = rulesQueryHandler.setWordsForRule(updatedRules, 0, words);
    }
    if (exact) {
        updatedRules = rulesQueryHandler.setExactForRule(updatedRules, 0, exact);
    }
    if (folders) {
        updatedRules = rulesQueryHandler.setFoldersForRule(updatedRules, 0, folders);
    }
    if (exactURLS) {
        updatedRules = rulesQueryHandler.setExactURLSForRule(updatedRules, 0, exactURLS);
    }
    return updatedRules[0];
};

/**
 * Removes a string from the currently edited rule
 * @param listType - The list type to remove the items from
 * @param items - The items to remove from the rule
 * @param currentRule - The rule currently edited
 */
export const removeItems = (listType: string, items: string[], currentRule: IRule) => {
    let updatedRules = [...[currentRule]];
    switch (listType) {
        case "words":
            updatedRules = rulesQueryHandler.removeWordsForRule(updatedRules, 0, items);
            break;
        case "exact":
            updatedRules = rulesQueryHandler.removeExactForRule(updatedRules, 0, items);
            break;
        case "folders":
            updatedRules = rulesQueryHandler.removeFoldersForRule(updatedRules, 0, items);
            break;
        case "exactURLS":
            updatedRules = rulesQueryHandler.removeExactURLSForRule(updatedRules, 0, items);
            break;
    }
    return updatedRules[0];
};

export const addEmptyRule = (ruleType: RuleTypes, segments: ISegmentRuleItem[]) => {
    const newRule = rulesQueryHandler.createNewRule(ruleType);
    segments.push(newRule);
};

export const getSegmentUrls = async (
    segmentRules: IRule[],
    segmentsApiService: SegmentsApiService,
    requestSegmentsPreview: (segmentsPreviewLoading: boolean) => void,
    selectedSite?: ISite,
): Promise<ISegmentUrl[]> => {
    const rulesWithContent = filterEmptyRules(segmentRules);
    const segmentsPreview = await getSegmentsPreview(
        rulesWithContent,
        segmentsApiService,
        requestSegmentsPreview,
        selectedSite,
    );
    return transformSegmentPreview(segmentsPreview.Data);
};

// Receives the unified segmentRules array and checks if at least one rule is defined with a non-empty filter type.
// If the array is bigger than 2 rules, it has to have non-empty,
// If the array holds exactly two rules, it might be the initial state where you have one empty rule for exclude, and one for include filter types.
export const doesOneNonEmptyRuleExist = (segmentRules: IRule[]) =>
    segmentRules.length > 2 ||
    (segmentRules.length === 2 &&
        (doesAtLeastOneRuleExist(segmentRules[segmentRules.length - 1]) ||
            doesAtLeastOneRuleExist(segmentRules[segmentRules.length - 2])));

export const getResponseBodyText = async (responseBody: ReadableStream): Promise<string> => {
    const reader = responseBody.getReader();
    const utf8Decoder = new TextDecoder("utf-8");
    const { value: chunk } = await reader.read();
    const responseMessage = chunk ? utf8Decoder.decode(chunk, { stream: true }) : "";
    reader.releaseLock();
    return responseMessage;
};

export const getPercentageChangeFromTooLongSegmentRuledResponseMessage = (responseMessage: string): string => {
    // extract to list all the numbers that the string contains
    const numbersArrayFromResponse = responseMessage.match(/^\d+|\d+\b|\d+(?=\w)/g);
    const exceedingNumber = Number(numbersArrayFromResponse[0]);
    const limitedNumber = Number(numbersArrayFromResponse[1]);
    const percentageChange = Math.round(Math.abs(((limitedNumber - exceedingNumber) / limitedNumber) * 100));
    return `${percentageChange}%`;
};
